// src/lib/badge-term.ts
//
// Prévia da validade do Selo antes de confirmar (back#143). ESPELHA
// lib/term.ts do back (addCalendarMonths / addDays / expiryForTerm), com os mesmos
// casos de teste: a tela mostra a data que o servidor vai gravar. Quem grava é
// o servidor — depois de salvar, a lista mostra o valor devolvido por ele.
//
// Validade é um dia de Brasília ("até 30/11" = fim de 30/11), gravado em UTC.
const BRASILIA_OFFSET_MS = -3 * 3_600_000;

function brasiliaDay(at: Date): { y: number; m: number; d: number } {
  const local = new Date(at.getTime() + BRASILIA_OFFSET_MS);
  return { y: local.getUTCFullYear(), m: local.getUTCMonth(), d: local.getUTCDate() };
}

function endOfBrasiliaDay(y: number, m: number, d: number): Date {
  return new Date(Date.UTC(y, m, d, 23, 59, 59, 999) - BRASILIA_OFFSET_MS);
}

const lastDayOfMonth = (y: number, m: number) => new Date(Date.UTC(y, m + 1, 0)).getUTCDate();

// Meses de calendário, preso ao último dia do mês (31/08 + 6 = 28/02).
export function addCalendarMonths(from: Date, months: number, day?: number): Date {
  const { y, m, d } = brasiliaDay(from);
  const target = m + months;
  const ty = y + Math.floor(target / 12);
  const tm = ((target % 12) + 12) % 12;
  return endOfBrasiliaDay(ty, tm, Math.min(day ?? d, lastDayOfMonth(ty, tm)));
}

// Dias corridos a partir do dia de Brasília de `from` → fim desse dia (trial).
export function addDays(from: Date, days: number): Date {
  const { y, m, d } = brasiliaDay(from);
  return endOfBrasiliaDay(y, m, d + days);
}

// Prazo: meses de calendário OU dias corridos (back: lib/term Term).
export type Term = { months: number } | { days: number };

// Prazo favorito: atalho ao dar ou renovar, com rótulo opcional ("Trial").
export type FavoriteTerm = Term & { label?: string };

// De fábrica (back: DEFAULT_FAVORITE_TERMS). É o que Selo novo recebe e o que
// a comunidade usa ao incluir aluno com validade (decisão de 2026-10-13).
export const DEFAULT_FAVORITE_TERMS: FavoriteTerm[] = [{ months: 6 }, { months: 12 }];

// Chave estável de um prazo ("m6", "d30"): 30 dias e 1 mês são prazos diferentes.
export const termKey = (t: Term): string => ("days" in t ? `d${t.days}` : `m${t.months}`);

// Duração aproximada, só para ordenar a lista como o back (mês ≈ 30,44 dias).
export const termApproxDays = (t: Term): number => ("days" in t ? t.days : t.months * 30.44);

// `current`: undefined = não é membro; null = sem validade. `anchor` = criação
// do vínculo. Mesmas regras do back: novo/sem validade → hoje; vencido → hoje
// no dia do cadastro; ainda vale → do vencimento (âncora só destrava o dia
// cortado por mês curto). Em dias: ainda vale → vencimento + dias; senão hoje + dias.
export function expiryForTerm(
  current: Date | null | undefined,
  term: Term,
  now: Date,
  anchor?: Date,
): Date {
  if ("days" in term) {
    return addDays(current && current.getTime() > now.getTime() ? current : now, term.days);
  }
  const { months } = term;
  if (current === undefined || current === null) return addCalendarMonths(now, months);
  if (current.getTime() <= now.getTime()) {
    return addCalendarMonths(now, months, anchor ? brasiliaDay(anchor).d : undefined);
  }
  const cur = brasiliaDay(current);
  const anchorDay = anchor ? brasiliaDay(anchor).d : cur.d;
  const clamped = cur.d === lastDayOfMonth(cur.y, cur.m) && anchorDay > cur.d;
  return addCalendarMonths(current, months, clamped ? anchorDay : cur.d);
}

// "6 meses", "1 ano", "18 meses", "2 anos", "30 dias" — ou o rótulo que o admin deu.
export function termLabel(t: Term & { label?: string }): string {
  if (t.label) return t.label;
  if ("days" in t) return t.days === 1 ? "1 dia" : `${t.days} dias`;
  if (t.months % 12 === 0) return t.months === 12 ? "1 ano" : `${t.months / 12} anos`;
  return t.months === 1 ? "1 mês" : `${t.months} meses`;
}

// Validade da API (ISO, UTC) como o dia de Brasília.
export function formatBadgeDate(iso: string | Date): string {
  return new Date(iso).toLocaleDateString("pt-BR", { timeZone: "America/Sao_Paulo" });
}

// Data e hora em Brasília, para eventos (Histórico e Origens do Selo).
export function formatBadgeDateTime(iso: string | Date): string {
  return new Date(iso).toLocaleString("pt-BR", {
    timeZone: "America/Sao_Paulo",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// AAAA-MM-DD do <input type="date"> → fim desse dia em Brasília (ISO).
export function endOfDayIso(day: string): string {
  return new Date(`${day}T23:59:59.999-03:00`).toISOString();
}
